/**
 * GuestCodesManagerPanel — Panneau Admin pour générer / gérer les codes Invité.
 *
 * Fonctionnalités :
 *   - Génération d'un code à 6 chiffres (à usage unique)
 *   - Liste des codes (actifs et consommés)
 *   - Désactivation manuelle (toggle)
 *   - Suppression définitive
 *   - Historique des sessions Invité (demandes + statuts)
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2,
  KeyRound,
  RefreshCw,
  Trash2,
  ShieldCheck,
  Gamepad2,
  ShieldQuestion,
  Copy,
  History,
  XCircle,
  CheckCircle2,
  Ban,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_BADGES = {
  manager: { label: "Resp Op", color: "bg-amber-500/20 text-amber-300 border-amber-500/40", Icon: ShieldCheck },
  coach_jeux: { label: "Coach", color: "bg-teal-500/20 text-teal-300 border-teal-500/40", Icon: Gamepad2 },
  both: { label: "Au choix", color: "bg-slate-500/20 text-slate-300 border-slate-500/40", Icon: ShieldQuestion },
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }); }
  catch { return "—"; }
};

export default function GuestCodesManagerPanel() {
  const [codes, setCodes] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [label, setLabel] = useState("");
  const [defaultRole, setDefaultRole] = useState("both");
  const [durationHours, setDurationHours] = useState(""); // "" = usage unique (fin de journée), sinon multi-usage sur X heures

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [codesRes, reqsRes] = await Promise.all([
        axios.get(`${API}/admin/guest-codes`, { params: { actor_role: "admin" } }),
        axios.get(`${API}/admin/guest-requests`, { params: { actor_role: "admin", limit: 30 } }),
      ]);
      setCodes(codesRes.data?.codes || []);
      setRequests(reqsRes.data?.requests || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const generate = async () => {
    setLoading(true);
    try {
      const payload = { label: label.trim(), default_role: defaultRole };
      const dh = parseInt(durationHours, 10);
      if (dh && dh > 0) payload.duration_hours = dh;
      const r = await axios.post(
        `${API}/admin/guest-codes/generate?actor_role=admin`,
        payload,
      );
      toast.success(`Code généré : ${r.data.code.code}`);
      setLabel("");
      setDurationHours("");
      fetchAll();
      // Copie automatique dans le presse-papier
      try { await navigator.clipboard.writeText(r.data.code.code); } catch {}
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de génération");
    } finally {
      setLoading(false);
    }
  };

  const copy = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success(`Code ${code} copié`);
    } catch {
      toast.error("Copie impossible");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer définitivement ce code ?")) return;
    setBusyId(id);
    try {
      await axios.delete(`${API}/admin/guest-codes/${id}?actor_role=admin`);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de suppression");
    } finally {
      setBusyId(null);
    }
  };

  const revokeSession = async (reqId) => {
    if (!window.confirm("Révoquer la session de cet invité ?")) return;
    setBusyId(reqId);
    try {
      await axios.post(`${API}/admin/guest-requests/${reqId}/revoke?actor_role=admin`);
      toast.success("Session révoquée");
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de révocation");
    } finally {
      setBusyId(null);
    }
  };

  const activeSessions = requests.filter((r) => r.status === "approved");
  const pendingCount = requests.filter((r) => r.status === "pending").length;

  return (
    <div className="space-y-4" data-testid="guest-codes-manager">
      <Card className="bg-slate-800/70 border-amber-500/30">
        <CardHeader className="pb-3">
          <CardTitle className="text-amber-400 flex items-center gap-2 text-lg">
            <KeyRound className="w-5 h-5" /> Générer un code Invité
          </CardTitle>
          <p className="text-slate-400 text-xs">
            Configure la durée d&apos;utilisation. Un code avec durée est <strong className="text-amber-300">multi-usage</strong> : plusieurs invités peuvent s&apos;y connecter tant qu&apos;il n&apos;est pas expiré.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-slate-300 text-xs">Libellé (optionnel)</Label>
              <Input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="ex: Coach Marc · Soir Sam"
                className="bg-slate-700 border-slate-600 text-white"
                maxLength={80}
                data-testid="guest-code-label-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-300 text-xs">Rôle autorisé</Label>
              <div className="grid grid-cols-3 gap-1.5">
                {Object.entries(ROLE_BADGES).map(([key, { label: lbl, Icon }]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setDefaultRole(key)}
                    className={`flex flex-col items-center gap-0.5 p-2 rounded-md border text-xs transition ${
                      defaultRole === key
                        ? "bg-amber-500/15 border-amber-500/60 text-amber-300"
                        : "bg-slate-700/40 border-slate-600 text-slate-300 hover:border-slate-400"
                    }`}
                    data-testid={`guest-role-filter-${key}`}
                  >
                    <Icon className="w-4 h-4" /> {lbl}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* 🆕 Durée d'utilisation */}
          <div className="space-y-1.5">
            <Label className="text-slate-300 text-xs flex items-center gap-1.5">
              <History className="w-3.5 h-3.5 text-amber-400" />
              Durée d&apos;utilisation
            </Label>
            <div className="flex flex-wrap gap-1.5">
              {[
                { v: "", lbl: "Usage unique", sub: "Fin de journée" },
                { v: "4", lbl: "4 h", sub: "" },
                { v: "12", lbl: "12 h", sub: "" },
                { v: "24", lbl: "24 h", sub: "1 jour" },
                { v: "72", lbl: "72 h", sub: "3 jours" },
                { v: "168", lbl: "168 h", sub: "1 semaine" },
              ].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setDurationHours(opt.v)}
                  className={`flex flex-col items-start px-3 py-1.5 rounded-md border text-[11px] font-semibold transition ${
                    durationHours === opt.v
                      ? "bg-amber-500/15 border-amber-500/60 text-amber-300"
                      : "bg-slate-700/40 border-slate-600 text-slate-300 hover:border-slate-400"
                  }`}
                  data-testid={`guest-duration-${opt.v || 'once'}`}
                >
                  <span>{opt.lbl}</span>
                  {opt.sub && <span className="text-[9.5px] text-slate-500">{opt.sub}</span>}
                </button>
              ))}
              <Input
                type="number"
                min={1}
                max={720}
                value={!["", "4", "12", "24", "72", "168"].includes(durationHours) ? durationHours : ""}
                onChange={(e) => setDurationHours(e.target.value)}
                placeholder="Heures"
                className="bg-slate-700 border-slate-600 text-white w-24 h-9 text-xs"
                data-testid="guest-duration-custom-input"
              />
            </div>
            <p className="text-[10.5px] text-slate-500 italic">
              {durationHours && parseInt(durationHours, 10) > 0
                ? `⏳ Le code sera valide et réutilisable pendant ${durationHours} h après sa génération.`
                : "🎫 Le code sera valide jusqu'à la fin de la journée (usage unique classique)."}
            </p>
          </div>

          <Button
            onClick={generate}
            disabled={loading}
            className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold"
            data-testid="guest-generate-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
            Générer un nouveau code
          </Button>
        </CardContent>
      </Card>

      {/* Codes actifs */}
      <Card className="bg-slate-800/70 border-slate-700">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-slate-200 text-base flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-amber-400" /> Codes ({codes.length})
          </CardTitle>
          <Button size="sm" variant="ghost" onClick={fetchAll} className="text-slate-400 hover:text-white">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </CardHeader>
        <CardContent>
          {codes.length === 0 ? (
            <p className="text-slate-500 text-xs italic text-center py-4">Aucun code généré pour le moment.</p>
          ) : (
            <div className="space-y-1.5 max-h-72 overflow-y-auto">
              {codes.map((c) => {
                const role = ROLE_BADGES[c.default_role || "both"] || ROLE_BADGES.both;
                const RoleIcon = role.Icon;
                return (
                  <div
                    key={c.id}
                    className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border ${
                      c.active ? "bg-emerald-900/20 border-emerald-700/40" : "bg-slate-800/40 border-slate-700"
                    }`}
                    data-testid={`guest-code-row-${c.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-white font-mono text-base tracking-wider">{c.code}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded border ${role.color} inline-flex items-center gap-1`}>
                          <RoleIcon className="w-3 h-3" /> {role.label}
                        </span>
                        {c.active ? (
                          <span className="text-[10px] text-emerald-300 inline-flex items-center gap-0.5">
                            <CheckCircle2 className="w-3 h-3" /> Disponible
                          </span>
                        ) : (
                          <span className="text-[10px] text-slate-400 inline-flex items-center gap-0.5">
                            <Ban className="w-3 h-3" /> Utilisé
                          </span>
                        )}
                        {/* 🆕 Badge multi-usage + expiration */}
                        {c.duration_hours ? (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded border inline-flex items-center gap-1 ${
                            c.is_expired
                              ? "bg-rose-500/15 border-rose-500/40 text-rose-300"
                              : "bg-amber-500/15 border-amber-500/40 text-amber-300"
                          }`}>
                            <History className="w-3 h-3" />
                            {c.is_expired ? "Expiré" : `Multi-usage · ${c.duration_hours}h`}
                          </span>
                        ) : (
                          <span className="text-[10px] px-1.5 py-0.5 rounded border bg-slate-500/15 border-slate-500/40 text-slate-300 inline-flex items-center gap-1">
                            Usage unique
                          </span>
                        )}
                      </div>
                      <p className="text-slate-400 text-[11px] truncate">
                        {c.label || "Sans libellé"} · créé le {formatDate(c.created_at)}
                        {c.duration_hours && c.expires_at ? ` · expire le ${formatDate(c.expires_at)}` : ""}
                        {c.consumed_at ? ` · consommé le ${formatDate(c.consumed_at)}` : ""}
                      </p>
                    </div>
                    {c.active && (
                      <Button size="icon" variant="ghost" onClick={() => copy(c.code)} className="h-7 w-7 text-slate-300 hover:text-amber-300" title="Copier">
                        <Copy className="w-3.5 h-3.5" />
                      </Button>
                    )}
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => remove(c.id)}
                      disabled={busyId === c.id}
                      className="h-7 w-7 text-slate-400 hover:text-rose-400"
                      title="Supprimer"
                      data-testid={`guest-code-delete-${c.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sessions actives + historique */}
      <Card className="bg-slate-800/70 border-slate-700">
        <CardHeader className="pb-2">
          <CardTitle className="text-slate-200 text-base flex items-center gap-2">
            <History className="w-4 h-4 text-amber-400" /> Sessions Invité
            {pendingCount > 0 && (
              <span className="text-[11px] text-rose-300 bg-rose-500/20 px-2 py-0.5 rounded border border-rose-500/40 animate-pulse">
                {pendingCount} en attente
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {requests.length === 0 ? (
            <p className="text-slate-500 text-xs italic text-center py-4">Aucune session invité enregistrée.</p>
          ) : (
            <div className="space-y-1.5 max-h-80 overflow-y-auto">
              {requests.map((r) => {
                const statusMap = {
                  pending: { color: "text-amber-300 bg-amber-500/15", label: "En attente" },
                  approved: { color: "text-emerald-300 bg-emerald-500/15", label: "Active" },
                  rejected: { color: "text-rose-300 bg-rose-500/15", label: "Refusée" },
                  revoked: { color: "text-slate-300 bg-slate-600/30", label: "Révoquée" },
                  expired: { color: "text-slate-400 bg-slate-600/30", label: "Expirée" },
                  logged_out: { color: "text-slate-400 bg-slate-600/30", label: "Déconnectée" },
                };
                const s = statusMap[r.status] || statusMap.expired;
                const isActive = r.status === "approved";
                const roleIcon = (r.approved_role || r.chosen_role) === "coach_jeux" ? Gamepad2 : ShieldCheck;
                const RoleIcon = roleIcon;
                return (
                  <div
                    key={r.id}
                    className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border ${
                      isActive ? "bg-emerald-900/15 border-emerald-700/40" : "bg-slate-800/40 border-slate-700"
                    }`}
                  >
                    <RoleIcon className={`w-4 h-4 ${isActive ? "text-emerald-300" : "text-slate-400"}`} />
                    <div className="min-w-0 flex-1">
                      <p className="text-white text-sm truncate">
                        {r.label || "Invité"} <span className="text-slate-500 font-mono text-[11px]">· {r.code_used}</span>
                      </p>
                      <p className="text-slate-400 text-[11px] truncate">
                        {formatDate(r.created_at)} → rôle {r.approved_role || r.chosen_role}
                      </p>
                    </div>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${s.color}`}>{s.label}</span>
                    {isActive && (
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => revokeSession(r.id)}
                        disabled={busyId === r.id}
                        className="h-7 w-7 text-rose-400 hover:bg-rose-500/20"
                        title="Révoquer la session"
                        data-testid={`guest-revoke-${r.id}`}
                      >
                        <XCircle className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
